/**
 * useCrudOperations Hook
 * 
 * Generic hook for CRUD operations with React Query.
 * Handles queries, mutations, notifications, and error handling.
 * Can be used across multiple apps.
 * 
 * @example
 * const {
 *   data,
 *   isLoading,
 *   error,
 *   createMutation,
 *   updateMutation,
 *   deleteMutation,
 * } = useCrudOperations({
 *   queryKey: 'campaign-types',
 *   service: campaignTypesService,
 *   entityName: 'Campaign Type',
 * });
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notify, logger } from '../services';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Generic service interface for CRUD operations
 */
export interface CrudService<T, CreateDto = Partial<T>, UpdateDto = Partial<T>> {
  getAll: () => Promise<T[]>;
  getById?: (id: number) => Promise<T>;
  create: (data: CreateDto) => Promise<T>;
  update: (id: number, data: UpdateDto) => Promise<T>;
  delete: (id: number) => Promise<void>;
  toggleActive?: (id: number) => Promise<T>;
}

export interface UseCrudOptions<T, CreateDto, UpdateDto> {
  /** Query key for React Query cache */
  queryKey: string;
  /** Service with CRUD methods */
  service: CrudService<T, CreateDto, UpdateDto>;
  /** Entity name for notifications (e.g., "Campaign Type") */
  entityName: string;
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
  /** Optional localized mutation notification messages. */
  messages?: CrudMutationMessages;
}

export interface CrudMutationMessages {
  createSuccess?: string;
  createError?: string;
  updateSuccess?: string;
  updateError?: string;
  deleteSuccess?: string;
  deleteError?: string;
  toggleSuccess?: string;
  toggleError?: string;
}

export interface UseCrudReturn<T, CreateDto, UpdateDto> {
  /** Data from the query */
  data: T[] | undefined;
  /** Whether the initial query is loading */
  isLoading: boolean;
  /** Error from the query */
  error: Error | null;
  /** Refetch the data */
  refetch: () => void;
  /** Create mutation */
  createMutation: {
    mutate: (data: CreateDto, callbacks?: MutationCallbacks<T>) => void;
    isPending: boolean;
  };
  /** Update mutation */
  updateMutation: {
    mutate: (params: { id: number; data: UpdateDto }, callbacks?: MutationCallbacks<T>) => void;
    isPending: boolean;
  };
  /** Delete mutation */
  deleteMutation: {
    mutate: (id: number, callbacks?: MutationCallbacks<void>) => void;
    isPending: boolean;
  };
  /** Toggle active mutation (if service supports it) */
  toggleActiveMutation?: {
    mutate: (id: number, callbacks?: MutationCallbacks<T>) => void;
    isPending: boolean;
  };
  /** Whether any mutation is in progress */
  isMutating: boolean;
}

interface MutationCallbacks<TData = void> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Extracts error message from API error response
 */
const getErrorMessage = (err: unknown, fallback: string): string => {
  const error = err as { response?: { data?: { message?: string } }; message?: string };
  return error.response?.data?.message || error.message || fallback;
};

// =============================================================================
// HOOK
// =============================================================================

export function useCrudOperations<T, CreateDto = Partial<T>, UpdateDto = Partial<T>>({
  queryKey,
  service,
  entityName,
  enabled = true,
  messages,
}: UseCrudOptions<T, CreateDto, UpdateDto>): UseCrudReturn<T, CreateDto, UpdateDto> {
  // Diagnostic: Log hook execution
  logger.info('[useCrudOperations] Hook called', { queryKey, entityName, enabled, hasService: !!service });
  const queryClient = useQueryClient();
  const queryKeyArray = [queryKey];


  // Diagnostic: Log before useQuery
  logger.info('[useCrudOperations] Setting up useQuery', { queryKeyArray, enabled });

  // Query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeyArray,
    queryFn: async () => {
      logger.info(`[useCrudOperations] queryFn executing for ${queryKey}`);

      try {
        const result = await service.getAll();
        logger.info(`[useCrudOperations] queryFn success for ${queryKey}`, { resultLength: result?.length ?? 0 });
        return result;
      } catch (err) {
        logger.error(`[useCrudOperations] queryFn error for ${queryKey}: ${(err as Error).message}`);
        throw err;
      }
    },
    enabled,
  });

  // Diagnostic: Log query state changes
  useEffect(() => {
    logger.info('[useCrudOperations] Query state changed', {
      queryKey,
      isLoading,
      hasData: !!data,
      dataLength: data?.length ?? 0,
      error: error?.message,
      enabled
    });
  }, [queryKey, isLoading, data, error, enabled]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (dto: CreateDto) => service.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyArray });
      notify.success(messages?.createSuccess ?? `${entityName} created successfully!`);
    },
    onError: (err: Error) => {
      notify.error(
        getErrorMessage(
          err,
          messages?.createError ?? `Failed to create ${entityName.toLowerCase()}`,
        ),
      );
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data: dto }: { id: number; data: UpdateDto }) =>
      service.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyArray });
      notify.success(messages?.updateSuccess ?? `${entityName} updated successfully!`);
    },
    onError: (err: Error) => {
      notify.error(
        getErrorMessage(
          err,
          messages?.updateError ?? `Failed to update ${entityName.toLowerCase()}`,
        ),
      );
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyArray });
      notify.success(messages?.deleteSuccess ?? `${entityName} deleted successfully!`);
    },
    onError: (err: Error) => {
      notify.error(
        getErrorMessage(
          err,
          messages?.deleteError ?? `Failed to delete ${entityName.toLowerCase()}`,
        ),
      );
    },
  });

  // Toggle active mutation (optional). The hook must run unconditionally to keep
  // a stable hook order; we only expose it when the service supports toggling.
  const toggleActiveMutationInternal = useMutation({
    mutationFn: (id: number) => service.toggleActive!(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyArray });
      notify.success(messages?.toggleSuccess ?? 'Status updated!');
    },
    onError: (err: Error) => {
      notify.error(getErrorMessage(err, messages?.toggleError ?? 'Failed to update status'));
    },
  });
  const toggleActiveMutation = service.toggleActive
    ? toggleActiveMutationInternal
    : undefined;

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    (toggleActiveMutation?.isPending ?? false);

  return {
    data,
    isLoading,
    error: error as Error | null,
    refetch,
    createMutation: {
      mutate: (dto: CreateDto, callbacks?: MutationCallbacks<T>) => {
        createMutation.mutate(dto, {
          onSuccess: callbacks?.onSuccess,
          onError: callbacks?.onError,
        });
      },
      isPending: createMutation.isPending,
    },
    updateMutation: {
      mutate: (params: { id: number; data: UpdateDto }, callbacks?: MutationCallbacks<T>) => {
        updateMutation.mutate(params, {
          onSuccess: callbacks?.onSuccess,
          onError: callbacks?.onError,
        });
      },
      isPending: updateMutation.isPending,
    },
    deleteMutation: {
      mutate: (id: number, callbacks?: MutationCallbacks<void>) => {
        deleteMutation.mutate(id, {
          onSuccess: callbacks?.onSuccess,
          onError: callbacks?.onError,
        });
      },
      isPending: deleteMutation.isPending,
    },
    toggleActiveMutation: toggleActiveMutation
      ? {
        mutate: (id: number, callbacks?: MutationCallbacks<T>) => {
          toggleActiveMutation.mutate(id, {
            onSuccess: callbacks?.onSuccess,
            onError: callbacks?.onError,
          });
        },
        isPending: toggleActiveMutation.isPending,
      }
      : undefined,
    isMutating,
  };
}

export default useCrudOperations;

